import { useCallback, useEffect, useRef, useState } from "react";
import { envConfig } from "Constant";
import { useTranslation } from "lib/i18n/use-translation";
import { useAuth } from "providers/auth";
import {
  getAccessTokenIfFresh,
  getFreshAccessToken,
} from "providers/auth-tokens";
import { logError } from "utility/Function";
import { coachFetch, CoachAuthError } from "apps/user/learner/api/coach";
import { authenticationFailed } from "providers/apollo-client";
import {
  OriInboundEvent,
  OriOutboundUserMessage,
} from "apps/user/learner/pages/chat/types";
import type { TapInWire } from "apps/user/learner/lib/tap-in";

export type ConnectionState =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error";

// QA live-template-testing (ori_qa feature flag): when present, the ticket
// request binds the resulting WS connection to a qa_test chat against this
// Configuration instead of the caller's normal conversation.
export interface OriQaConnectionParams {
  configId: string;
  // The bounded thread this connection resumes. Threads are created up front
  // (POST /api/qa/threads/), so the socket always targets one by id — that is
  // what lets several coexist and be switched between.
  threadId: string;
  // The conversation the thread was opened at. Nothing reads it now that the
  // transcript covers the whole thread (see useOriChat), and it is not sent on
  // the ticket either.
  conversationId: string;
}

interface UseOriConnectionOptions {
  onEvent?: (event: OriInboundEvent) => void;
  enabled?: boolean;
  qa?: OriQaConnectionParams | null;
}

export interface SendOptions {
  tapIn?: TapInWire;
  capabilities?: string[];
}

interface UseOriConnectionReturn {
  connectionState: ConnectionState;
  isConnected: boolean;
  reconnect: () => void;
  sendMessage: (
    message: string,
    clientLastMessageId?: string | null,
    options?: SendOptions
  ) => void;
}

const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 10000;
const MAX_RECONNECT_ATTEMPTS = 5;

// A socket stuck CONNECTING past this is closed so the existing
// onclose→scheduleReconnect backoff runs, instead of relying on the
// browser's own (much longer, opaque) connect timeout.
const CONNECT_TIMEOUT = 15000;

const CLOSE_TICKET_EXPIRED = 4002;
const CLOSE_TICKET_INVALID = 4003;

interface TicketResponse {
  ticket: string;
  expires_in: number;
}

function toWsOrigin(httpOrigin: string): string {
  return httpOrigin.replace(/^http/, "ws");
}

export default function useOriConnection(
  options: UseOriConnectionOptions = {}
): UseOriConnectionReturn {
  const { authToken } = useAuth();
  const { i18n } = useTranslation();
  const enabled = options.enabled ?? true;
  const qa = options.qa ?? null;
  // Primitive key (not the `qa` object's identity) so a freshly-constructed
  // options object every render doesn't force a spurious reconnect — only an
  // actual config/thread change does.
  const qaKey = qa ? `${qa.configId}:${qa.threadId}` : "";

  const onEventRef = useRef(options.onEvent);
  onEventRef.current = options.onEvent;

  const [connectionState, setConnectionState] =
    useState<ConnectionState>("idle");

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  const isMountedRef = useRef(true);
  const hasConnectedRef = useRef(false);

  // Held in a ref so the reconnect scheduler always calls the latest connect.
  const connectRef = useRef<() => Promise<void>>(async () => {});

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearConnectTimer = useCallback(() => {
    if (connectTimerRef.current !== null) {
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!isMountedRef.current) return;

    if (attemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
      setConnectionState("error");
      return;
    }

    const delay = Math.min(
      INITIAL_RECONNECT_DELAY * 2 ** attemptRef.current,
      MAX_RECONNECT_DELAY
    );
    attemptRef.current += 1;
    setConnectionState("reconnecting");

    clearReconnectTimer();
    reconnectTimerRef.current = setTimeout(() => {
      void connectRef.current();
    }, delay);
  }, [clearReconnectTimer]);

  const fetchTicket = useCallback(
    () =>
      coachFetch<TicketResponse>("/api/ws/ticket/", {
        method: "POST",
        ...(qa
          ? {
              body: {
                qa: true,
                qa_config_id: qa.configId,
                qa_thread_id: qa.threadId,
              },
            }
          : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qaKey]
  );

  const connect = useCallback(async () => {
    if (!isMountedRef.current) return;

    setConnectionState((prev) =>
      prev === "reconnecting" ? prev : "connecting"
    );

    let ticketResponse: TicketResponse;
    try {
      ticketResponse = await fetchTicket();
    } catch (error) {
      if (error instanceof CoachAuthError) {
        // Re-auth instead of burning the backoff budget on a Retry that 401s.
        authenticationFailed();
        return;
      }
      logError("Ori ticket exchange failed", error);
      if (isMountedRef.current) scheduleReconnect();
      return;
    }

    if (!isMountedRef.current) return;

    const env = envConfig.PLATFORM_ENV ?? "";
    const wsOrigin = toWsOrigin(envConfig.COACH_URL ?? "");
    const url = `${wsOrigin}/ws/chat/?ticket=${encodeURIComponent(
      ticketResponse.ticket
    )}&env=${encodeURIComponent(env)}`;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch (error) {
      // A malformed URL (e.g. unset COACH_URL) throws without firing onclose,
      // so backoff would never run — fail hard instead.
      logError("Ori WebSocket could not be opened", error);
      setConnectionState("error");
      return;
    }
    socketRef.current = socket;

    clearConnectTimer();
    connectTimerRef.current = setTimeout(() => {
      connectTimerRef.current = null;
      if (socketRef.current === socket && socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    }, CONNECT_TIMEOUT);

    socket.onopen = () => {
      clearConnectTimer();
      if (!isMountedRef.current) return;
      attemptRef.current = 0;
      hasConnectedRef.current = true;
      setConnectionState("connected");
    };

    socket.onmessage = (e) => {
      try {
        onEventRef.current?.(JSON.parse(e.data));
      } catch (err) {
        logError("Ori received a malformed WebSocket frame", err);
      }
    };

    socket.onerror = () => {
      // Just log; the subsequent onclose drives the backoff.
      logError(
        "Ori WebSocket error",
        new Error("Ori WebSocket reported an error")
      );
    };

    socket.onclose = (event) => {
      // A stale, already-superseded socket can still fire this belatedly —
      // only touch the watchdog if it's still the one connectTimerRef guards.
      if (socketRef.current === socket) clearConnectTimer();
      if (!isMountedRef.current) return;
      if (socketRef.current === socket) socketRef.current = null;

      const ticketRejected =
        event.code === CLOSE_TICKET_EXPIRED ||
        event.code === CLOSE_TICKET_INVALID;
      if (ticketRejected && hasConnectedRef.current) {
        // A previously-live connection lost its ticket — reconnect immediately
        // with a fresh one. Gated on a prior open so repeated rejections on a
        // never-opened connection can't reset the counter and loop forever.
        attemptRef.current = 0;
      }
      hasConnectedRef.current = false;

      scheduleReconnect();
    };
  }, [clearConnectTimer, fetchTicket, scheduleReconnect]);

  connectRef.current = connect;

  const reconnect = useCallback(() => {
    clearReconnectTimer();
    attemptRef.current = 0;
    void connectRef.current();
  }, [clearReconnectTimer]);

  // Ref so the offline/online listeners never re-subscribe on a fresh reconnect.
  const reconnectRef = useRef(reconnect);
  reconnectRef.current = reconnect;

  const sendMessage = useCallback(
    (
      message: string,
      clientLastMessageId?: string | null,
      { tapIn, capabilities }: SendOptions = {}
    ) => {
      const socket = socketRef.current;
      if (socket?.readyState !== WebSocket.OPEN) {
        logError(
          "Ori sendMessage called while the socket was not open",
          new Error("Ori WebSocket is not open")
        );
        return;
      }
      const frame: OriOutboundUserMessage = { type: "user_message", message };
      if (clientLastMessageId) {
        frame.client_last_message_id = clientLastMessageId;
      }
      if (tapIn) {
        frame.entry_point = tapIn.entryPoint;
        frame.context = tapIn.context;
      }
      if (capabilities) {
        frame.capabilities = capabilities;
      }
      // Coach pins the token forwarded at connect time; a long chat
      // outlives its 15-minute TTL, breaking Platform-backed tools.
      // Attach the current token so Coach can adopt it per message —
      // synchronously when it's still fresh, after a refresh otherwise.
      const freshToken = getAccessTokenIfFresh();
      if (freshToken) {
        frame.platform_token = freshToken;
        socket.send(JSON.stringify(frame));
        return;
      }
      void getFreshAccessToken()
        .then((refreshed) => {
          if (refreshed) {
            frame.platform_token = refreshed;
          }
        })
        .catch(() => {
          // A failed refresh must never drop the user's message - send
          // token-less; Coach keeps the connect-time token.
        })
        .finally(() => {
          // Re-check: the refresh can outlive the socket.
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(frame));
          }
        });
    },
    []
  );

  // The OAuth token is minted asynchronously after mount, so wait for it.
  // Keying the effect on the boolean (not the token) avoids tearing down a
  // live socket on rotation — reconnect fetches read the latest via the ref.
  const hasToken = Boolean(authToken);

  useEffect(() => {
    if (!hasToken || !enabled) return undefined;

    isMountedRef.current = true;
    attemptRef.current = 0;
    hasConnectedRef.current = false;
    void connectRef.current();

    // A DevTools "Offline" throttle leaves the socket in readyState OPEN, so
    // onclose never fires — force-close it to run the existing backoff path.
    const handleOffline = () => {
      socketRef.current?.close();
    };
    // Guard on a dropped socket so a spurious online can't double-connect.
    const handleOnline = () => {
      if (!socketRef.current) reconnectRef.current();
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      isMountedRef.current = false;
      clearReconnectTimer();
      clearConnectTimer();
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.onopen = null;
        socketRef.current.close();
        socketRef.current = null;
      }
    };
    // qaKey re-runs this effect (tear down + reconnect with a fresh ticket)
    // whenever the QA config or thread changes. i18n.language does the same
    // for a language change — Coach reads the language preference once at
    // connect, and LocaleSync drives i18n.language from the menu/profile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken, enabled, qaKey, i18n.language]);

  return {
    connectionState,
    isConnected: connectionState === "connected",
    reconnect,
    sendMessage,
  };
}
